import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../../lib/api';
import { LocationBreadcrumb } from '../location/location-breadcrumb';
import { ArchiveConfirmDialog } from '../location/location-inspector';
import { LocationTreeSelect } from '../location/location-tree-select';
import type { LocationNode, LocationTypeItem } from '../location/types';
import type { GeometryPatch } from './geometry-authority';
import type { Geometry, LayoutObject } from './types';

/**
 * S4 右侧属性面板。
 *
 * **§7 明确要求**属性面板提供 `Parent Location: [树选择器]` 作为**显式**改父入口——
 * 这是画布上"拖入即提示"之外的第二条、也是唯一另一条改层级的路径。两条路径都
 * 最终调用 `POST /locations/:id/move`（写审计、防环、跨仓库校验），几何端点
 * 自始至终碰不到层级。
 */
export function LayoutInspector({
  object,
  warehouseId,
  geometry,
  canEditGeometry,
  canManage,
  canArchive,
  onGeometryChange,
  onStructureChanged,
  onClose,
  onNavigate,
}: {
  readonly object: LayoutObject;
  readonly warehouseId: string;
  readonly geometry: Geometry;
  readonly canEditGeometry: boolean;
  readonly canManage: boolean;
  readonly canArchive: boolean;
  /**
   * M3B 独立复审第三轮 BLOCKER 1：这里**只允许**发出几何值补丁
   * （x/y/w/h/locked），绝不发完整的 `Geometry`——完整对象会把"编辑那一刻"
   * 的 `version` 快照一路带进撤销命令历史，undo 时被服务端当成版本冲突拒绝
   * （第二轮只修了画布路径，属性面板正是从这里漏掉的）。
   */
  readonly onGeometryChange: (patch: GeometryPatch) => void;
  readonly onStructureChanged: () => void;
  readonly onClose: () => void;
  readonly onNavigate: (id: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const locationId = object.locationId;

  const { data: node } = useQuery({
    queryKey: ['locations', 'detail', locationId],
    queryFn: () => apiFetch<LocationNode>(`/locations/${locationId}`),
  });
  const { data: typesData } = useQuery({
    queryKey: ['location-types'],
    queryFn: () => apiFetch<{ items: LocationTypeItem[] }>('/location-types'),
  });

  const [name, setName] = useState(object.name);
  const [typeId, setTypeId] = useState('');
  const [canStore, setCanStore] = useState(object.canStoreInventory);
  const [error, setError] = useState<string | null>(null);
  const [pendingParent, setPendingParent] = useState<{ id: string | null } | null>(null);
  const [showArchive, setShowArchive] = useState(false);

  useEffect(() => {
    setName(object.name);
    setCanStore(object.canStoreInventory);
    setTypeId(object.typeId ?? '');
  }, [object]);

  const showError = (err: unknown): void =>
    setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));

  const saveProperties = async (): Promise<void> => {
    if (!node) return;
    setError(null);
    try {
      await apiFetch(`/locations/${locationId}`, {
        method: 'PATCH',
        body: {
          name,
          canStoreInventory: canStore,
          typeId: typeId || undefined,
          version: node.version,
        },
      });
      onStructureChanged();
    } catch (err) {
      showError(err);
    }
  };

  const confirmReparent = async (): Promise<void> => {
    if (!pendingParent) return;
    setError(null);
    try {
      await apiFetch(`/locations/${locationId}/move`, {
        method: 'POST',
        body: { newParentId: pendingParent.id },
      });
      setPendingParent(null);
      onStructureChanged();
    } catch (err) {
      setPendingParent(null);
      showError(err);
    }
  };

  const geometryField = (key: 'x' | 'y' | 'w' | 'h'): React.JSX.Element => (
    <label className="field field--inline" key={key}>
      <span>{t(`layout.geometry.${key}`)}</span>
      <input
        type="number"
        value={geometry[key]}
        disabled={!canEditGeometry || geometry.locked}
        onChange={(e) => {
          const value = Math.round(Number(e.target.value));
          if (!Number.isFinite(value)) return;
          onGeometryChange(
            key === 'w' || key === 'h' ? { [key]: Math.max(1, value) } : { [key]: value },
          );
        }}
      />
    </label>
  );

  return (
    <aside className="layout-panel">
      <div className="row row--between">
        <LocationBreadcrumb locationId={locationId} onNavigate={onNavigate} />
        <button className="btn btn--ghost btn--sm" onClick={onClose}>
          {t('app.close')}
        </button>
      </div>
      <h2 className="layout-panel__title">{object.name}</h2>

      <label className="field">
        <span>{t('locations.name')}</span>
        <input value={name} onChange={(e) => setName(e.target.value)} disabled={!canManage} />
      </label>

      <label className="field">
        <span>{t('locations.type')}</span>
        <select value={typeId} onChange={(e) => setTypeId(e.target.value)} disabled={!canManage}>
          <option value="">—</option>
          {(typesData?.items ?? []).map((type) => (
            <option key={type.id} value={type.id}>
              {t(type.nameKey, type.code)}
            </option>
          ))}
        </select>
      </label>

      <label className="row" style={{ marginBottom: 'var(--space-3)' }}>
        <input
          type="checkbox"
          checked={canStore}
          onChange={(e) => setCanStore(e.target.checked)}
          disabled={!canManage}
        />
        {t('locations.canStoreInventory')}
      </label>

      {/* §7：属性面板里的显式改父入口 */}
      <div className="field">
        <span>{t('locations.parent')}</span>
        {canManage ? (
          <LocationTreeSelect
            warehouseId={warehouseId}
            value={node?.parentId ?? null}
            onChange={(id) => {
              if (id !== (node?.parentId ?? null)) setPendingParent({ id });
            }}
            excludeSubtreeOf={locationId}
          />
        ) : (
          <p className="field-hint">{t('layout.readOnly')}</p>
        )}
      </div>

      <div className="layout-panel__geometry">
        {(['x', 'y', 'w', 'h'] as const).map(geometryField)}
      </div>

      <label className="row" style={{ marginBottom: 'var(--space-3)' }}>
        <input
          type="checkbox"
          checked={geometry.locked}
          disabled={!canEditGeometry}
          onChange={(e) => onGeometryChange({ locked: e.target.checked })}
        />
        {t('layout.lockPosition')}
      </label>

      {error && <p className="field-error">{error}</p>}

      <div className="row">
        {canManage && (
          <button className="btn btn--primary btn--sm" onClick={() => void saveProperties()}>
            {t('app.save')}
          </button>
        )}
        {canArchive && (
          <button className="btn btn--danger btn--sm" onClick={() => setShowArchive(true)}>
            {t('locations.archive')}
          </button>
        )}
      </div>

      {/* 【U-02】说明:位置没有直接删除——归档是常规出口;归档后管理员可在
          位置树(显示已归档)中永久删除无历史引用的位置 */}
      <p className="field-hint" style={{ marginTop: 'var(--space-2)' }}>
        {t('locations.archiveDeleteNote')}
      </p>

      {pendingParent && (
        <div className="modal-overlay" onClick={() => setPendingParent(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{t('locations.confirmMoveTitle')}</h2>
            <p>{t('layout.explicitReparentBody', { name: object.name })}</p>
            <div className="modal__actions">
              <button className="btn btn--ghost" onClick={() => setPendingParent(null)}>
                {t('app.cancel')}
              </button>
              <button className="btn btn--primary" onClick={() => void confirmReparent()}>
                {t('app.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showArchive && (
        <ArchiveConfirmDialog
          locationId={locationId}
          locationName={object.name}
          onClose={() => setShowArchive(false)}
          onArchived={() => {
            setShowArchive(false);
            onStructureChanged();
            onClose();
          }}
        />
      )}
    </aside>
  );
}
